import { useDispatch, useSelector } from 'react-redux';

import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
// A ruler rather than a car: the distance metric applies just as much to a
// warehouse floor as to a road network.
import StraightenRoundedIcon from '@mui/icons-material/StraightenRounded';
import {
  Box,
  Radio,
  Typography,
} from '@mui/material';
import styled from 'styled-components';

import { updateGeneralSetting, updateTableSetting } from 'store/reducers/inputReducer';
import { selectCostSetting } from 'store/reducers/selectors/settingsSelectors.js';

const Container = styled.div`
  border: 1.5px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 4px;
`;

const Header = styled.div`
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f0f0f0;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 20px;
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid ${props => props.$last ? 'transparent' : '#f5f5f5'};
  background: ${props => props.$active ? 'rgba(0, 146, 139, 0.04)' : 'transparent'};

  &:hover {
    background: ${props => props.$active ? 'rgba(0, 146, 139, 0.06)' : '#fafafa'};
  }
`;

const IconBox = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  background: ${props => props.$active ? '#017C76' : '#f4f5f7'};
  color: ${props => props.$active ? '#fff' : '#525f7f'};
  font-size: 18px;
  transition: all 0.15s ease;
`;

const OptimizationChoice = () => {
  const dispatch = useDispatch();
  const costMetric = useSelector(selectCostSetting({ settingName: 'costMetric' }));

  const handleOptionSelect = (value) => {
    dispatch(updateGeneralSetting({ settingName: 'costs', subSettingName: 'costMetric', value }));

    if (value === 'duration') {
      dispatch(updateTableSetting({ tableName: 'travel_costs', settingName: 'isViewable', newValue: true, columnName: 'duration' }));
      dispatch(updateTableSetting({ tableName: 'travel_costs', settingName: 'isViewable', newValue: false, columnName: 'distance' }));
    }

    if (value === 'distance') {
      dispatch(updateTableSetting({ tableName: 'travel_costs', settingName: 'isViewable', newValue: true, columnName: 'distance' }));
    }
  };

  // Wording stays independent of the setting: routes may run on roads, but
  // equally across a warehouse floor or an x-y grid, so nothing here should
  // assume driving.
  const options = [
    {
      label: 'Travel Distance',
      description: 'Minimize the total distance covered',
      value: 'distance',
      icon: <StraightenRoundedIcon fontSize="small" />,
    },
    {
      label: 'Travel Duration',
      description: 'Minimize the total time spent traveling',
      value: 'duration',
      icon: <AccessTimeRoundedIcon fontSize="small" />,
    },
    {
      label: 'Manual Entry',
      description: 'Enter your own custom cost values',
      value: 'custom',
      icon: <EditRoundedIcon fontSize="small" />,
    },
  ];

  return (
    <Container>
      <Header>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#32325d', mb: 0.25 }}>
          Select optimization metric
        </Typography>
        <Typography variant="body2" sx={{ color: '#525f7f', fontSize: '0.8rem' }}>
          Choose what the solver should minimize.
        </Typography>
      </Header>

      {options.map((option, idx) => {
        const active = costMetric === option.value;
        return (
          <OptionRow
            key={option.value}
            $active={active}
            $last={idx === options.length - 1}
            onClick={() => handleOptionSelect(option.value)}
          >
            <IconBox $active={active}>
              {option.icon}
            </IconBox>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: active ? '#017C76' : '#32325d', lineHeight: 1.3 }}
              >
                {option.label}
              </Typography>
              <Typography variant="body2" sx={{ color: '#525f7f', fontSize: '0.78rem' }}>
                {option.description}
              </Typography>
            </Box>

            <Radio
              checked={active}
              size="small"
              sx={{ color: '#ced4da', '&.Mui-checked': { color: '#017C76' }, p: 0 }}
            />
          </OptionRow>
        );
      })}
    </Container>
  );
};

export default OptimizationChoice;
